import { memo } from "react";

import useImageUtils from "~/hooks/useImageUtils";
import type { Image as ImageType } from "~/lib/app.types";
import { cn } from "~/lib/ui";

import Image from "../ui/image";

const ProductImageComponent = ({
  image,
  lazy,
  small,
  className,
  type = "plp",
  ref,
  ...props
}: React.ComponentProps<"picture"> & {
  image?: ImageType;
  lazy?: boolean;
  small?: boolean;
  type?: "plp" | "pdp";
}) => {
  const { getImageMediaLink } = useImageUtils();
  return image ? (
    <picture className={cn("h-full w-full", className)} ref={ref} {...props}>
      {small ? (
        // Small images are used for the product list page
        <source
          srcSet={getImageMediaLink(image, {
            params: {
              sm: "edge",
              "resize.edge": "w",
              q: "85",
              "resize.edge.length": "400",
              fmt: "auto",
            },
          })}
          media="(min-width:767px)"
        />
      ) : (
        <source
          srcSet={getImageMediaLink(image, {
            params: {
              sm: "edge",
              "resize.edge": "w",
              q: "85",
              "resize.edge.length": "300",
              fmt: "auto",
            },
          })}
          media="(min-width:600)"
        />
      )}
      <source
        srcSet={`${getImageMediaLink(image, {
          params: {
            sm: "edge",
            "resize.edge": "w",
            q: "85",
            "resize.edge.length": "700",
            fmt: "auto",
          },
        })}`}
        media="(min-width:1024px)"
      />
      <Image
        src={getImageMediaLink(image, {
          type,
          params: {
            fmt: "auto",
          },
        })}
        height={320}
        width={320}
        alt={image.alt || "image"}
        className={cn(
          "block h-full w-full object-cover object-center",
          className,
        )}
        loading="lazy"
      />
    </picture>
  ) : null;
};
ProductImageComponent.displayName = "ProductImageComponent";

export const ProductImage = memo(
  ProductImageComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.image?.link === nextProps.image?.link &&
      prevProps.lazy === nextProps.lazy &&
      prevProps.small === nextProps.small &&
      prevProps.className === nextProps.className &&
      prevProps.type === nextProps.type
    );
  },
);
ProductImage.displayName = "ProductImage";
